import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Play, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useFeaturedLesson } from "@/hooks/useFeaturedLesson";
import { useBooks } from "@/hooks/useBooks";
import LessonPreviewPlayer from "@/components/LessonPreviewPlayer";
import { resolveLessonPrice, useLessonsPromoUntil } from "@/lib/lessonsPromo";

/* ── Tile renderers ─────────────────────────────────────────── */

const LessonComposition = () => (
  <svg viewBox="0 0 400 400" className="w-full h-full" aria-hidden>
    <circle cx="200" cy="200" r="140" fill="hsl(var(--ink))" />
    <circle cx="200" cy="200" r="60" fill="hsl(var(--cream))" />
    <circle cx="200" cy="200" r="22" fill="hsl(var(--yellow))" />
    <circle cx="320" cy="90" r="14" fill="hsl(var(--cream))" />
    <circle cx="80" cy="320" r="10" fill="hsl(var(--cream))" />
  </svg>
);

const ZemelapisComposition = () => (
  <svg viewBox="0 0 400 400" className="w-full h-full" aria-hidden>
    <rect x="40" y="40" width="320" height="320" rx="24" fill="hsl(var(--cream))" />
    <path d="M80 220 Q200 80 320 220 T320 320" stroke="hsl(var(--ink))" strokeWidth="6" fill="none" />
    <circle cx="120" cy="260" r="14" fill="hsl(var(--terracotta))" />
    <circle cx="280" cy="160" r="14" fill="hsl(var(--yellow))" />
    <circle cx="200" cy="200" r="10" fill="hsl(var(--ink))" />
  </svg>
);

const LessonTile = ({
  lesson,
  onPreview,
  onBuy,
}: {
  lesson: any;
  onPreview: () => void;
  onBuy: () => void;
}) => {
  const promoUntil = useLessonsPromoUntil();
  const pricing = resolveLessonPrice(lesson, promoUntil);
  const onSale = pricing.onSale;
  const price = pricing.amount.toFixed(2).replace(".", ",");

  return (
    <div className="rounded-3xl bg-terracotta text-cream overflow-hidden shadow-[0_30px_60px_-30px_rgba(224,74,47,0.45)] flex flex-col">
      <div className="aspect-[4/3] p-8 flex items-center justify-center">
        <div className="w-full max-w-[220px]">
          <LessonComposition />
        </div>
      </div>
      <div className="p-7 md:p-8 flex flex-col flex-1">
        <span className="text-[10px] tracking-[0.22em] uppercase text-cream/70 font-semibold mb-3">
          Audio pamoka
        </span>
        <h3 className="font-marquee text-2xl md:text-3xl font-bold leading-tight mb-3 text-cream">
          {lesson.title}
        </h3>
        <p className="text-cream/85 text-sm leading-relaxed mb-5 line-clamp-3">
          {lesson.description ?? "Audio pamoka apie save."}
        </p>
        <div className="text-xs text-cream/70 mb-5">
          {lesson.duration ?? "~50 min"} · €{price}
          {onSale && (
            <span className="ml-2 line-through opacity-60">
              €{pricing.regularPrice.toFixed(2).replace(".", ",")}
            </span>
          )}
        </div>
        <div className="mt-auto flex flex-col sm:flex-row gap-2">
          <Button
            onClick={onPreview}
            className="h-11 px-5 rounded-full text-sm font-semibold bg-cream text-ink hover:bg-cream/90"
          >
            <Play size={14} className="mr-1" />
            Klausyti 1 min
          </Button>
          <Button
            onClick={onBuy}
            variant="outline"
            className="h-11 px-5 rounded-full text-sm font-semibold border-cream/40 text-cream bg-transparent hover:bg-cream/10"
          >
            Įsigyti <ArrowRight size={14} />
          </Button>
        </div>
      </div>
    </div>
  );
};

const BookTile = ({ book, onOpen }: { book: any; onOpen: () => void }) => {
  const price = Number(book.price ?? 0).toFixed(2).replace(".", ",");
  return (
    <div className="rounded-3xl bg-ink text-cream overflow-hidden shadow-[0_30px_60px_-30px_rgba(0,0,0,0.45)] flex flex-col">
      <div className="aspect-[4/3] p-8 flex items-center justify-center bg-cream-soft">
        {book.cover_url ? (
          <img
            src={book.cover_url}
            alt={book.title}
            className="max-h-full max-w-[200px] object-contain drop-shadow-xl"
            loading="lazy"
          />
        ) : (
          <div className="w-40 h-56 rounded-md bg-ink/20" />
        )}
      </div>
      <div className="p-7 md:p-8 flex flex-col flex-1">
        <span className="text-[10px] tracking-[0.22em] uppercase text-cream/70 font-semibold mb-3">
          Knyga
        </span>
        <h3 className="font-marquee text-2xl md:text-3xl font-bold leading-tight mb-3 text-cream">
          {book.title}
        </h3>
        {book.author && (
          <p className="text-cream/70 text-sm mb-3">{book.author}</p>
        )}
        <p className="text-cream/85 text-sm leading-relaxed mb-5 line-clamp-3">
          {book.description ?? "Knyga, kuri augina."}
        </p>
        <div className="text-xs text-cream/70 mb-5">€{price}</div>
        <div className="mt-auto">
          <Button
            onClick={onOpen}
            className="h-11 px-5 rounded-full text-sm font-semibold bg-cream text-ink hover:bg-cream/90"
          >
            Žiūrėti knygą <ArrowRight size={14} />
          </Button>
        </div>
      </div>
    </div>
  );
};

const ZemelapisTile = () => {
  const navigate = useNavigate();
  return (
    <div className="rounded-3xl bg-yellow text-ink overflow-hidden shadow-[0_30px_60px_-30px_rgba(0,0,0,0.25)] flex flex-col">
      <div className="aspect-[4/3] p-8 flex items-center justify-center">
        <div className="w-full max-w-[220px]">
          <ZemelapisComposition />
        </div>
      </div>
      <div className="p-7 md:p-8 flex flex-col flex-1">
        <span className="text-[10px] tracking-[0.22em] uppercase text-ink/60 font-semibold mb-3">
          Asmeninis žemėlapis
        </span>
        <h3 className="font-marquee text-2xl md:text-3xl font-bold leading-tight mb-3">
          Tavo Žemėlapis
        </h3>
        <p className="text-ink/75 text-sm leading-relaxed mb-5 line-clamp-3">
          Asmeninis portretas — atsakymai apie tave, sugeneruoti į PDF, kurį
          gali laikyti šalia.
        </p>
        <div className="mt-auto">
          <Button
            onClick={() => navigate("/zemelapis")}
            className="h-11 px-5 rounded-full text-sm font-semibold bg-ink text-cream hover:bg-ink/85"
          >
            Pradėti
          </Button>
        </div>
      </div>
    </div>
  );
};

/* ── Main ───────────────────────────────────────────────────── */

type Slot = "book" | "lecture" | "zemelapis";
const PAIRS: [Slot, Slot][] = [
  ["book", "lecture"],
  ["lecture", "zemelapis"],
  ["zemelapis", "book"],
];

const RecommendedPair = () => {
  const navigate = useNavigate();
  const [previewOpen, setPreviewOpen] = useState(false);

  const { data: lesson, isLoading: lessonLoading } = useFeaturedLesson();
  const { data: books } = useBooks();

  // Pick a random pair once per mount
  const pair = useMemo(() => PAIRS[Math.floor(Math.random() * PAIRS.length)], []);

  // Pick a random featured book once per mount
  const book = useMemo(() => {
    if (!books || books.length === 0) return null;
    return books[Math.floor(Math.random() * books.length)];
  }, [books]);

  if (lessonLoading) {
    return (
      <section className="bg-cream py-20">
        <div className="container mx-auto px-6 text-center text-ink/40">Įkeliama...</div>
      </section>
    );
  }

  const renderSlot = (slot: Slot) => {
    if (slot === "lecture" && lesson) {
      return (
        <LessonTile
          key="lecture"
          lesson={lesson}
          onPreview={() => setPreviewOpen(true)}
          onBuy={() => navigate(`/pamokos?lesson=${lesson.id}`)}
        />
      );
    }
    if (slot === "book" && book) {
      return (
        <BookTile
          key="book"
          book={book}
          onOpen={() =>
            navigate(book.slug ? `/knygynas/${book.slug}` : `/knygynas?book=${book.id}`)
          }
        />
      );
    }
    if (slot === "zemelapis") {
      return <ZemelapisTile key="zemelapis" />;
    }
    return null;
  };

  return (
    <section className="bg-cream py-16 md:py-24">
      <div className="container mx-auto px-6">
        <div className="text-center mb-10">
          <span className="text-[11px] tracking-[0.22em] uppercase text-ink/55 font-semibold">
            — Rekomenduojame —
          </span>
        </div>

        <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          {pair.map((slot) => renderSlot(slot))}
        </div>
      </div>

      {lesson && (
        <LessonPreviewPlayer
          open={previewOpen}
          onOpenChange={setPreviewOpen}
          lessonId={lesson.id}
          lessonTitle={lesson.title}
        />
      )}
    </section>
  );
};

export default RecommendedPair;
